// 숙련(마스터리)·초월 강화 게임 연결(3개월 루프 C4, 정본 docs/PROGRESSION-720H.md 4·6절, 데이터 src/shared/mastery.ts).
// 숙련 EXP = 이 캐릭터의 모든 성장 원장(영웅·회차별 growth 키)에서 Lv99 누적 EXP를 넘은 몫의 합. 저장하지 않고 원장에서 다시 센다.
// 배분·재분배 횟수·부위 초월·이정표는 장비 장부(gear.v1 GearState.ms)에 연산으로 쓴다 — 골드·강화석과 한 장부라 반쪽 결과가 없다.
// 원장 읽기는 마을 명령·마을 진입·로드 때만 한다(프레임 루프 아님).
import { GROWTH_KEY, GROWTH_LYRA_KEY, ledgerXp, migrateLedgerCurveV3, parseLedger } from './growth';
import { REBIRTH_GROWTH_KEYS } from './rebirth';
import type { SaveStorage } from './save';
import type { GameSim } from './game';
import { ENHANCE_MAX } from '../shared/content/world';
import { SLOT_ORDER, type GearCode, type GearOp } from '../shared/gearLedger';
import {
  EMPTY_MASTERY, MASTERY_MILESTONES, TRANSCEND_MAX, TRANSCEND_STEPS, claimableMilestones, fitMastery, makeMasteryStats, masteryPoints, masteryProgress,
  masteryStats, masterySum, milestoneClaimed, overflowXp, respecCost, transcendChance, transcendUnlocked,
  type MasteryAlloc, type MasteryLastView, type MasterySlotView, type MasteryStatsOut, type MasteryView,
} from '../shared/mastery';
import type { EquipSlot } from '../shared/content/types';
import { weaponUsableBy, type GearItem } from '../shared/gear';
import { Ev } from '../shared/events';
import { Scene, type GameCommand } from '../shared/types';

/** 숙련 EXP를 세는 성장 원장 키(영웅 0회차 + 환생 회차). 새 영웅·회차 키가 생기면 여기에 더한다 */
export const MASTERY_SOURCE_KEYS: readonly string[] = [GROWTH_KEY, GROWTH_LYRA_KEY, ...REBIRTH_GROWTH_KEYS.kael, ...REBIRTH_GROWTH_KEYS.lyra];

/** 저장소의 성장 원장들에서 숙련 EXP를 센다(읽기 전용, 손상·미래 버전 원장은 0으로 본다) */
export function masteryXpFrom(storage: SaveStorage): number {
  let x = 0;
  for (const k of MASTERY_SOURCE_KEYS) {
    let raw: string | null = null;
    try { raw = storage.getItem(k); } catch { raw = null; }
    if (raw === null) continue;
    const p = parseLedger(raw);
    // 곡선 이관 전 원장(환생 전 회차 키는 GrowthStore가 열지 않아 이관되지 않는다)도 지금 곡선으로 맞춘 뒤 넘친 몫을 센다(Codex P2 #286)
    if (p.ok) x += overflowXp(ledgerXp(migrateLedgerCurveV3(p.ledger)));
  }
  return x;
}

export type MasteryCmd = Extract<GameCommand, { type: 'masteryAlloc' | 'transcend' | 'masteryClaim' }>;
type Mutable<T> = { -readonly [K in keyof T]: T[K] };

export class MasteryController {
  /** 마지막으로 센 숙련 EXP(원장 합) */
  private xp = 0;
  private rev = 0;
  private readonly fx: MasteryStatsOut = makeMasteryStats();
  private readonly active: boolean[] = [false, false, false, false, false, false];
  private readonly slotViews: Mutable<MasterySlotView>[] = SLOT_ORDER.map((slot) => ({ slot, step: 0, fails: 0, active: false, chance: 0, stones: 0, gold: 0 }));
  readonly last: MasteryLastView = { seq: 0, kind: 'alloc', code: 'ok', slot: '', ok: false, from: 0, to: 0, chance: 0, gold: 0 };
  readonly view: MasteryView;

  constructor(private readonly g: GameSim, private readonly storage: SaveStorage) {
    const self = this;
    this.view = {
      get revision() { return self.rev + self.g.gear.revision * 7; },
      get level() { return masteryProgress(self.xp).level; },
      get into() { return masteryProgress(self.xp).into; },
      get need() { return masteryProgress(self.xp).need; },
      get points() { return self.points; },
      get alloc() { return self.alloc(); },
      get free() { return Math.max(0, self.points - masterySum(self.alloc())); },
      get respecs() { return self.ms().r; },
      get respecCost() { return respecCost(self.ms().r); },
      get unlock() { return self.unlock; },
      get slots() { return self.slotsView(); },
      get claimable() { return claimableMilestones(masteryProgress(self.xp).level, self.ms().cl); },
      get claimed() { return self.ms().cl; },
      last: this.last,
    };
    this.refresh();
  }

  private ms() { return this.g.gear.ms; }
  get level(): number { return masteryProgress(this.xp).level; }
  get points(): number { return masteryPoints(this.level); }
  /** 효과에 쓰는 배분(포인트 안으로 줄인 것) */
  alloc(): MasteryAlloc { const a = this.ms().a; return a ? fitMastery(a, this.points) : EMPTY_MASTERY; }
  /** 환생 회차는 계정에서 가장 높은 영웅 기준(1차 환생 = +16, 2차 = +17) */
  get unlock(): number { return transcendUnlocked(Math.max(this.g.rebirth.cycleOf('kael'), this.g.rebirth.cycleOf('lyra')), this.level); }

  /** 원장에서 숙련 EXP를 다시 센다. 바뀌었으면 true(로드·마을 진입·숙련 명령 전) */
  refresh(): boolean {
    const x = masteryXpFrom(this.storage);
    if (x === this.xp) return false;
    this.xp = x; this.rev++;
    return true;
  }

  private slotsView(): readonly MasterySlotView[] {
    const s = this.ms(), eq = this.g.gear.equipped;
    for (let i = 0; i < SLOT_ORDER.length; i++) {
      const v = this.slotViews[i], step = s.tr[i];
      const uid = eq[SLOT_ORDER[i]];
      const it = uid ? this.g.gear.items.find((x) => x.uid === uid) : undefined;
      v.step = step; v.fails = s.tf[i]; v.active = !!it && it.enh >= ENHANCE_MAX && this.usable(i, it);
      const next = TRANSCEND_STEPS[Math.min(TRANSCEND_MAX - 1, step)];
      v.chance = step >= TRANSCEND_MAX ? 0 : transcendChance(step, s.tf[i]).chance;
      v.stones = step >= TRANSCEND_MAX ? 0 : next.stones; v.gold = step >= TRANSCEND_MAX ? 0 : next.gold;
    }
    return this.slotViews;
  }

  /** 부위 i(장착 개체 it)를 지금 영웅이 쓰는가: 무기 칸은 계정 공유라 다른 영웅 전용 무기는 빠진다(gearTotalsOf와 같은 규칙, Codex P2 #286, lyra-weapon-damage) */
  private usable(i: number, it: GearItem): boolean { return SLOT_ORDER[i] !== 'weapon' || weaponUsableBy(it, this.g.heroId); }

  /** 전투 보정: 숙련 배분 + 초월(그 부위 장비가 +15일 때만). derive 직전에 부른다. 할당 없음 */
  stats(): MasteryStatsOut {
    const eq = this.g.gear.equipped, items = this.g.gear.items;
    for (let i = 0; i < SLOT_ORDER.length; i++) {
      const uid = eq[SLOT_ORDER[i]];
      let on = false;
      if (uid) for (const it of items) if (it.uid === uid) { on = this.usable(i, it) && it.enh >= ENHANCE_MAX; break; }
      this.active[i] = on;
    }
    return masteryStats(this.fx, this.alloc(), this.ms().tr, this.active);
  }

  /** 명령: 배분(마을, 일시정지 아님) · 초월(대장간 창) · 이정표 받기(마을) */
  command(cmd: MasteryCmd): void {
    this.refresh();
    const g = this.g, f = g.flow;
    const kind = cmd.type === 'masteryAlloc' ? 'msAlloc' : cmd.type === 'transcend' ? 'transcend' : 'msClaim';
    const code = g.gear.command(kind, cmd.requestId, (id): GearOp | GearCode => {
      if (f.paused || f.scene !== Scene.Village) return 'denied';
      if (cmd.type === 'transcend') { const d = g.village.gearDenial('enhance'); if (d) return d; }
      // 골드가 움직이는 연산은 거래 권한(이 탭이 상점 장부 writer)이 있을 때만(장비 명령과 같은 규칙)
      if (g.profile.mode !== 'ok') return 'readonly';
      const gold = g.profile.gold;
      switch (cmd.type) {
        case 'masteryAlloc': return { id, t: 'msAlloc', a: cmd.alloc, points: this.points, gold };
        case 'transcend': return { id, t: 'transcend', slot: cmd.slot, unlock: this.unlock, gold, ...(cmd.slot === 'weapon' && this.g.heroId !== 'kael' ? { hero: this.g.heroId } : {}) };
        case 'masteryClaim': return { id, t: 'msClaim', idx: cmd.idx, level: this.level };
      }
    });
    if (code === 'duplicate') return;
    const l = this.last, res = g.gear.lastApply;
    l.seq++; l.kind = cmd.type === 'masteryAlloc' ? 'alloc' : cmd.type === 'transcend' ? 'transcend' : 'claim'; l.code = code;
    l.slot = cmd.type === 'transcend' ? cmd.slot : ''; l.gold = code === 'ok' && res ? res.gold ?? 0 : 0;
    if (cmd.type === 'transcend' && code === 'ok') {
      // 이 연산의 실제 판정을 보고한다: gear.command는 저장소 최신으로 refresh한 뒤 적용하므로 res.tr가 그 판정이다
      // (장부 전후 차이로 보면 다른 탭의 성공을 이번 성공으로 보일 수 있다, Codex P2 #286)
      const t = res?.tr;
      l.ok = !!t && t.ok; l.from = t ? t.from : 0; l.to = t ? t.to : 0; l.chance = t ? t.chance : 0;
      const en = g.emit(Ev.GearEnhance);
      en.a = l.ok ? 0 : 1; en.b = ENHANCE_MAX + l.to; en.c = ENHANCE_MAX + l.from; en.d = l.chance;
    } else { l.ok = code === 'ok'; l.from = 0; l.to = 0; l.chance = 0; }
    const e = g.emit(Ev.GearCmd);
    e.a = code === 'ok' ? 1 : 0;
    e.b = cmd.type === 'masteryAlloc' ? 13 : cmd.type === 'transcend' ? 14 : 15;
    this.rev++;
    // 골드 잔액을 바로 맞춘다(같은 틱의 다음 유료 명령이 옛 잔액으로 승인되지 않게, gearCommand와 같은 순서 — Codex P1 #286)
    g.syncGear();
    if (code === 'ok') g.syncGrowth();
  }

  /** 시험·UI: 이정표 i를 받았는가 */
  claimed(i: number): boolean { return milestoneClaimed(this.ms().cl, i); }
  /** 시험: 이정표 수 */
  static readonly MILESTONES = MASTERY_MILESTONES.length;
}
